/**
 * Browser page translation (Chrome/Google Translate, Edge) replaces text nodes with its own
 * <font> elements. React still holds the original nodes and crashes on the next update with
 * "Failed to execute 'removeChild' on 'Node'" (or 'insertBefore'), which takes the whole page
 * down via the error boundary. See https://github.com/facebook/react/issues/11538.
 *
 * The guard makes both calls tolerant: a node that is no longer a child of the given parent is
 * left alone instead of throwing. Normal calls behave exactly as before.
 */
let installed = false;

export const guardDomAgainstExternalChanges = (): void => {
  if (installed || typeof Node !== 'function' || !Node.prototype) {
    return;
  }
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn('[DOM guard] removeChild skipped, node was moved by an external change (e.g. page translation)');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn(
        '[DOM guard] insertBefore skipped, reference node was moved by an external change (e.g. page translation)',
      );
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
};
